// Recensement d'un marché à ciel ouvert (D-305 à D-307) : avancement par îlot et par agent, doublons
// signalés à trancher (jamais fusionnés automatiquement), liste des commerçants née du recensement ou
// d'un import, attribution préparée depuis une place recensée.
import { Bandeau, Bouton, Champ, DateChamp, EtatVide, LienBouton, Panneau } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { INVALIDER_MARCHES, useMarche } from './api';
import { aujourdhuiIso } from './logique';
import {
  attribuerRecensement,
  deciderRecensement,
  useCommercants,
  useRecensement,
  type AvancementRecensement,
  type Commercant,
} from './plan-api';
import styles from './marches.module.css';

function Doublon({
  d,
  peutDecider,
}: {
  d: AvancementRecensement['doublons'][number];
  peutDecider: boolean;
}) {
  const [motif, setMotif] = useState('');
  const action = useAction({
    executer: (decision: 'retenir' | 'ecarter' | 'distinct' | 'meme_personne') =>
      deciderRecensement(d.id, { decision, motif }),
    succes: 'Décision enregistrée.',
    invalider: INVALIDER_MARCHES,
  });
  const occupant = d.motif === 'occupant_different';
  return (
    <li className={styles.pile}>
      <p>
        <span className="cc-mono">{d.place.reference}</span> :{' '}
        {occupant
          ? `nouveau passage « ${d.statut === 'libre' ? 'place libre' : (d.occupant ?? '')} » différent du recensement retenu « ${d.concurrent?.statut === 'libre' ? 'place libre' : (d.concurrent?.occupant ?? '')} »`
          : `« ${d.occupant ?? ''} » porte le nom d’un commerçant déjà recensé${d.homonyme?.place ? ` sur la place ${d.homonyme.place}` : ''}`}
        , reçu le {date(d.recu_le)}.
      </p>
      {peutDecider ? (
        <div className={styles.barreFiltres}>
          <Champ
            libelle="Motif de la décision"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            maxLength={300}
          />
          {occupant ? (
            <>
              <Bouton
                petit
                variante="secondaire"
                disabled={motif.trim().length < 3 || action.enCours}
                onClick={() => action.lancer('retenir')}
              >
                Retenir ce passage
              </Bouton>
              <Bouton
                petit
                variante="discret"
                disabled={motif.trim().length < 3 || action.enCours}
                onClick={() => action.lancer('ecarter')}
              >
                Écarter ce passage
              </Bouton>
            </>
          ) : (
            <>
              <Bouton
                petit
                variante="secondaire"
                disabled={motif.trim().length < 3 || action.enCours}
                onClick={() => action.lancer('distinct')}
              >
                Personnes distinctes
              </Bouton>
              <Bouton
                petit
                variante="discret"
                disabled={motif.trim().length < 3 || action.enCours}
                onClick={() => action.lancer('meme_personne')}
              >
                Même personne, deux places
              </Bouton>
            </>
          )}
        </div>
      ) : null}
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </li>
  );
}

function PreparerAttribution({ c }: { c: Commercant }) {
  const [debut, setDebut] = useState(aujourdhuiIso());
  const [fin, setFin] = useState('');
  const action = useAction({
    executer: () => attribuerRecensement(c.recensement_id!, { periode: { debut, fin } }),
    succes: (r) =>
      `Attribution ${r.numero} enregistrée ; la première redevance est émise par le module des recettes.`,
    invalider: INVALIDER_MARCHES,
  });
  return (
    <div className={styles.barreFiltres}>
      <DateChamp libelle="Début" value={debut} onChange={(e) => setDebut(e.target.value)} />
      <DateChamp libelle="Fin" value={fin} onChange={(e) => setFin(e.target.value)} />
      <Bouton petit disabled={!fin || action.enCours} onClick={() => action.lancer(undefined)}>
        Attribuer la place {c.place?.reference}
      </Bouton>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </div>
  );
}

export function PageRecensement() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const marche = useMarche(id);
  const avancement = useRecensement(id);
  const [q, setQ] = useState('');
  const commercants = useCommercants(id, q);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const peutDecider = peut(contexte, 'marche_municipal', 'modifier');
  const peutAttribuer = peut(contexte, 'marche_municipal', 'creer');
  const a = avancement.data;
  return (
    <>
      <EnTetePage
        etiquette={marche.data ? `Marché ${marche.data.code}` : 'Marché'}
        titre="Recensement et commerçants"
        description={
          marche.data ? (
            <span>
              {marche.data.nom} : recensement place par place, doublons à trancher et liste des
              commerçants.
            </span>
          ) : null
        }
        actions={
          <LienBouton href={`/marches/${id}`} icone={ArrowLeft}>
            Fiche du marché
          </LienBouton>
        }
      />
      <EtatRequete
        chargement={avancement.isPending}
        erreur={avancement.error}
        onReessayer={() => void avancement.refetch()}
        lot="plan précis"
        fonction="Le recensement du marché"
      >
        {a ? (
          <div className={styles.pile}>
            <Panneau titre="Avancement par îlot" niveau={2}>
              <table className={styles.tableauPlan}>
                <caption>Places recensées par îlot</caption>
                <thead>
                  <tr>
                    <th scope="col">Îlot</th>
                    <th scope="col">Places</th>
                    <th scope="col">Recensées</th>
                    <th scope="col">Occupées</th>
                    <th scope="col">Libres</th>
                  </tr>
                </thead>
                <tbody>
                  {a.ilots.map((i) => (
                    <tr key={i.id ?? 'hors'}>
                      <th scope="row">{i.code ? `${i.code} · ${i.nom}` : i.nom}</th>
                      <td>{i.places}</td>
                      <td>{i.recensees}</td>
                      <td>{i.occupees}</td>
                      <td>{i.libres}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panneau>
            <Panneau titre="Avancement par agent" niveau={2}>
              {a.agents.length ? (
                <table className={styles.tableauPlan}>
                  <caption>Passages de recensement par agent</caption>
                  <thead>
                    <tr>
                      <th scope="col">Agent</th>
                      <th scope="col">Passages</th>
                      <th scope="col">Places</th>
                      <th scope="col">Doublons signalés</th>
                      <th scope="col">Dernier envoi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.agents.map((g) => (
                      <tr key={g.id ?? g.nom}>
                        <th scope="row">{g.nom}</th>
                        <td>{g.passages}</td>
                        <td>{g.places}</td>
                        <td>{g.doublons}</td>
                        <td>{date(g.dernier)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className={styles.aide}>Aucun passage reçu de l’application terrain.</p>
              )}
            </Panneau>
            <Panneau titre={`Doublons à trancher (${a.doublons.length})`} niveau={2}>
              {a.doublons.length ? (
                <ul className={styles.pile}>
                  {a.doublons.map((d) => (
                    <Doublon key={d.id} d={d} peutDecider={peutDecider} />
                  ))}
                </ul>
              ) : (
                <p className={styles.aide}>Aucun doublon signalé.</p>
              )}
            </Panneau>
            <Panneau titre="Commerçants" niveau={2}>
              <Champ
                libelle="Rechercher un nom ou une place"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                maxLength={100}
              />
              {commercants.data?.length ? (
                <ul className={styles.pile}>
                  {commercants.data.map((c) => (
                    <li key={c.id}>
                      <strong>{c.nom}</strong>
                      {c.activite ? `, ${c.activite}` : ''} ·{' '}
                      {c.place ? (
                        <span className="cc-mono">{c.place.reference}</span>
                      ) : (
                        'sans place'
                      )}{' '}
                      · {c.source === 'recensement' ? 'recensé' : 'importé'}
                      {c.doublon_signale ? ' · doublon possible signalé' : ''}
                      {c.attribution ? (
                        <>
                          {' · '}
                          <LienBouton
                            petit
                            variante="discret"
                            href={`/marches/attributions/${c.attribution.id}`}
                          >
                            Attribution {c.attribution.numero}
                          </LienBouton>
                        </>
                      ) : c.recensement_id && c.place && peutAttribuer ? (
                        <>
                          {' '}
                          <Bouton
                            petit
                            variante="discret"
                            aria-pressed={ouvert === c.id}
                            onClick={() => setOuvert(ouvert === c.id ? null : c.id)}
                          >
                            Préparer l’attribution
                          </Bouton>
                          {ouvert === c.id ? <PreparerAttribution c={c} /> : null}
                        </>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <EtatVide titre="Aucun commerçant" niveau={3}>
                  <p>
                    La liste se remplit par le recensement sur le terrain ou par l’import d’une
                    liste CSV.
                  </p>
                </EtatVide>
              )}
            </Panneau>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
